import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, Check, X, Clock, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { getAuthErrorMessage, loginInputClass } from "../LoginForm";
import { primaryButtonClass } from "./ProfilTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
const TYPES = ["Igazolás", "Kikérő", "E-igazolás", "E-kikérő"]; // identical to /hianyzas
const isIgazolas = (t) => t === "Igazolás" || t === "E-igazolás";
const dateConstraints = (t) => {
  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  return isIgazolas(t) ? { max: today } : { min: tomorrow };
};
const dateLabel = (t) => `Dátum ${isIgazolas(t) ? "(mai vagy korábbi)" : "(holnaptól)"}`;
const fetchReasons = () => axios.get(`${API}/hianyzas/reasons`).then((r) => (r.data || []).map((x) => (typeof x === "string" ? x : x.name)));
const STATUS = { pending: ["Jóváhagyásra vár", "bg-yellow-100 text-yellow-900"], approved: ["Jóváhagyva", "bg-emerald-100 text-emerald-800"], rejected: ["Elutasítva", "bg-red-100 text-red-700"] };
const card = "rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100";

export const openPdf = async (req) => {
  try {
    const res = await axios.get(`${API}/ugyfelkapu/hianyzas/requests/${req.id}/pdf`, { params: { token: token() }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank");
  } catch (err) {
    toast.error("A PDF nem tölthető le");
  }
};
export const downloadPdf = async (req) => {
  try {
    const res = await axios.get(`${API}/ugyfelkapu/hianyzas/requests/${req.id}/pdf`, { params: { token: token() }, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    a.download = `${req.type}_${req.date}.pdf`;
    a.click();
  } catch (err) {
    toast.error("A PDF nem tölthető le");
  }
};

export const RequestRow = ({ r, children }) => (
  <li className="flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center" data-testid={`hianyzas-row-${r.id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-slate-800">{r.user_name} <span className="font-normal text-slate-700">· {r.class_name}</span></p>
      <p className="text-xs text-slate-700">{r.type} · {r.date} · {r.reason}{r.requester_name && r.requester_id !== r.user_id ? ` · kérte: ${r.requester_name}` : ""}</p>
    </div>
    <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>
  </li>
);

const PdfButtons = ({ r }) => (
  <>
    <button type="button" onClick={() => openPdf(r)} className="inline-flex items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100" data-testid={`hianyzas-open-${r.id}`}><FileText size={14} /> Megnyitás</button>
    <button type="button" onClick={() => downloadPdf(r)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid={`hianyzas-download-${r.id}`}><Download size={14} /> Letöltés</button>
  </>
);

export function HianyzasTab({ user }) {
  const [persons, setPersons] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ type: "E-igazolás", person_id: user?.id || "", class_number: "", class_letter: "", date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [adminAdd, setAdminAdd] = useState(false);

  const load = async () => {
    try {
      const P = { params: { token: token() } };
      const [p, r, q] = await Promise.all([
        axios.get(`${API}/ugyfelkapu/hianyzas/persons`, P), fetchReasons(),
        axios.get(user?.is_admin ? `${API}/ugyfelkapu/admin/hianyzas/requests?status=approved` : `${API}/ugyfelkapu/hianyzas/requests`, P),
      ]);
      setPersons(p.data.persons || []); setReasons(r); setRequests(q.data || []);
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.class_number.trim() || !form.date || !form.reason) { toast.error("Osztály, dátum és indok megadása kötelező"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ugyfelkapu/hianyzas/requests`, form, { params: { token: token() } });
      toast.success("Kérelem beküldve, jóváhagyásra vár");
      setForm({ ...form, class_number: "", class_letter: "", date: "", reason: "" });
      load();
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy(false);
  };

  if (user?.is_admin) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-6" data-testid="hianyzas-tab">
        <section className={card}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-slate-900">Jóváhagyott igazolások</h2>
            <button type="button" onClick={() => setAdminAdd(true)} className={primaryButtonClass} data-testid="hianyzas-admin-add-btn"><Plus size={16} /> Hiányzás hozzáadása</button>
          </div>
          {requests.length === 0 ? <p className="py-6 text-center text-sm text-slate-600" data-testid="hianyzas-approved-empty">Még nincs jóváhagyott igazolás.</p> : (
            <ul className="divide-y divide-slate-100" data-testid="hianyzas-approved-list">{requests.map((r) => <RequestRow key={r.id} r={r}><PdfButtons r={r} /></RequestRow>)}</ul>
          )}
        </section>
        <ReasonsManager />
        {adminAdd && <AdminAddModal onClose={() => setAdminAdd(false)} onDone={() => { setAdminAdd(false); load(); }} />}
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="mx-auto w-full max-w-2xl space-y-6" data-testid="hianyzas-tab">
      <form onSubmit={submit} className={`${card} space-y-4`} data-testid="hianyzas-form">
        <h2 className="text-lg font-bold text-slate-900">Új igazolás / kikérő igénylése</h2>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Kinek?</label>
          <select value={form.person_id} onChange={set("person_id")} className={loginInputClass} data-testid="hianyzas-person-select">
            {persons.map((p) => <option key={p.id} value={p.id}>{p.name}{p.self ? " (saját)" : ""}</option>)}
          </select></div>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Típus</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, date: "" })} className={loginInputClass} data-testid="hianyzas-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Osztály *</label>
            <input value={form.class_number} onChange={set("class_number")} placeholder="pl. 12" className={loginInputClass} data-testid="hianyzas-class-number" /></div>
          <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Évfolyam</label>
            <input value={form.class_letter} onChange={set("class_letter")} placeholder="pl. A" className={loginInputClass} data-testid="hianyzas-class-letter" /></div>
        </div>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">{dateLabel(form.type)} *</label>
          <input type="date" value={form.date} onChange={set("date")} {...dateConstraints(form.type)} className={loginInputClass} data-testid="hianyzas-date" /></div>
        <div><label className="mb-1.5 block text-sm font-medium text-slate-700">Indok *</label>
          <select value={form.reason} onChange={set("reason")} className={loginInputClass} data-testid="hianyzas-reason-select">
            <option value="">Válassz...</option>{reasons.map((r) => <option key={r} value={r}>{r}</option>)}
          </select></div>
        <button type="submit" disabled={busy} className={`${primaryButtonClass} h-12 w-full`} data-testid="hianyzas-submit">{busy ? "Küldés..." : "Kérelem beküldése"}</button>
      </form>
      <section className={card} data-testid="hianyzas-my-list">
        <h2 className="mb-3 text-lg font-bold text-slate-900">Kérelmeim</h2>
        {requests.length === 0 ? <p className="py-4 text-center text-sm text-slate-600">Még nincs kérelmed.</p> : (
          <ul className="divide-y divide-slate-100">{requests.map((r) => (
            <RequestRow key={r.id} r={r}>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS[r.status]?.[1]}`} data-testid={`hianyzas-status-${r.id}`}>{STATUS[r.status]?.[0] || r.status}</span>
              {r.status === "approved" && r.pdf_path && <PdfButtons r={r} />}
            </RequestRow>
          ))}</ul>
        )}
      </section>
    </div>
  );
}

function AdminAddModal({ onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({ type: "E-igazolás", person_id: "", class_number: "", class_letter: "", date: "", reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const P = { params: { token: token() } };
    axios.get(`${API}/competition/users`, P).then((r) => setUsers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "hu"))));
    fetchReasons().then(setReasons);
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.person_id || !form.class_number.trim() || !form.date || !form.reason) { toast.error("Név, osztály, dátum és indok kötelező"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ugyfelkapu/admin/hianyzas/requests`, form, { params: { token: token() } });
      toast.success(res.data.request?.status === "approved" ? "Hiányzás rögzítve és jóváhagyva, PDF elkészült" : "Rögzítve – a PDF feltöltése után hagyható jóvá (/hianyzas admin)");
      onDone();
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="hianyzas-admin-modal">
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-slate-900">Hiányzás hozzáadása</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100" data-testid="hianyzas-admin-close"><X size={22} /></button></div>
        <select value={form.person_id} onChange={set("person_id")} className={loginInputClass} data-testid="hianyzas-admin-person">
          <option value="">Név...</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}{u.identifier ? ` (${u.identifier})` : ""}{u.is_child_account ? " – gyerek" : ""}</option>)}
        </select>
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, date: "" })} className={loginInputClass} data-testid="hianyzas-admin-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.class_number} onChange={set("class_number")} placeholder="Osztály * (pl. 12)" className={loginInputClass} data-testid="hianyzas-admin-class-number" />
          <input value={form.class_letter} onChange={set("class_letter")} placeholder="Évfolyam (pl. A)" className={loginInputClass} data-testid="hianyzas-admin-class-letter" />
        </div>
        <label className="block text-xs font-medium text-slate-700">{dateLabel(form.type)}</label>
        <input type="date" value={form.date} onChange={set("date")} {...dateConstraints(form.type)} className={loginInputClass} data-testid="hianyzas-admin-date" />
        <select value={form.reason} onChange={set("reason")} className={loginInputClass} data-testid="hianyzas-admin-reason"><option value="">Oka...</option>{reasons.map((r) => <option key={r} value={r}>{r}</option>)}</select>
        <button type="submit" disabled={busy} className="h-12 w-full rounded-xl bg-emerald-600 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid="hianyzas-admin-save">{busy ? "Mentés..." : "Mentés"}</button>
      </form>
    </div>
  );
}

function ReasonsManager() {
  const [reasons, setReasons] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const P = { params: { token: token() } };
  const load = () => axios.get(`${API}/ugyfelkapu/admin/hianyzas/reasons`, P).then((r) => setReasons(r.data || []));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (err) { toast.error(getAuthErrorMessage(err)); } };
  return (
    <section className={card} data-testid="reasons-manager">
      <h2 className="mb-4 text-lg font-bold text-slate-900">Indokok kezelése</h2>
      <form onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; run(() => axios.post(`${API}/ugyfelkapu/admin/hianyzas/reasons`, { name }, P), "Indok hozzáadva"); setName(""); }} className="mb-4 flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Új indok megnevezése" className={loginInputClass} data-testid="reason-new-input" />
        <button type="submit" className={`${primaryButtonClass} h-12 shrink-0`} data-testid="reason-add-btn"><Plus size={16} /> Hozzáadás</button>
      </form>
      <ul className="divide-y divide-slate-100" data-testid="reasons-list">
        {reasons.map((r) => (
          <li key={r.id} className="flex items-center gap-2 py-2.5 text-sm" data-testid={`reason-row-${r.id}`}>
            {editing?.id === r.id ? (
              <>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={`${loginInputClass} h-10`} data-testid="reason-edit-input" />
                <button type="button" onClick={() => { run(() => axios.put(`${API}/ugyfelkapu/admin/hianyzas/reasons/${r.id}`, { name: editing.name }, P), "Indok átnevezve"); setEditing(null); }} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white" data-testid="reason-edit-save">Mentés</button>
                <button type="button" onClick={() => setEditing(null)} className="rounded-lg border px-3 py-2 text-xs">Mégse</button>
              </>
            ) : (
              <>
                <span className={`flex-1 ${r.active ? "text-slate-800" : "text-slate-600 line-through"}`}>{r.name}</span>
                <button type="button" onClick={() => run(() => axios.put(`${API}/ugyfelkapu/admin/hianyzas/reasons/${r.id}`, { active: !r.active }, P), r.active ? "Indok elrejtve" : "Indok aktiválva")} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`} data-testid={`reason-toggle-${r.id}`}>{r.active ? "Aktív" : "Rejtett"}</button>
                <button type="button" onClick={() => setEditing({ id: r.id, name: r.name })} className="rounded-lg p-1.5 text-sky-700 hover:bg-sky-50" data-testid={`reason-edit-${r.id}`}><Pencil size={15} /></button>
                <button type="button" onClick={() => { if (window.confirm(`Törlöd: ${r.name}?`)) run(() => axios.delete(`${API}/ugyfelkapu/admin/hianyzas/reasons/${r.id}`, P), "Indok törölve"); }} className="rounded-lg p-1.5 text-red-600 hover:bg-red-50" data-testid={`reason-delete-${r.id}`}><Trash2 size={15} /></button>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function HianyzasAdminPending() {
  const [reqs, setReqs] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => axios.get(`${API}/ugyfelkapu/admin/hianyzas/requests`, { params: { token: token(), status: "pending" } }).then((r) => setReqs(r.data)).catch(() => setReqs([]));
  useEffect(() => { load(); }, []);
  const upload = async (r, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Csak PDF fájl tölthető fel"); return; }
    setBusy(r.id);
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/ugyfelkapu/admin/hianyzas/requests/${r.id}/upload-pdf`, fd, { params: { token: token() } });
      toast.success(r.pdf_path ? "PDF lecserélve" : "PDF feltöltve");
      load();
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy("");
  };
  const decide = async (r, decision) => {
    setBusy(r.id);
    try {
      await axios.post(`${API}/ugyfelkapu/admin/hianyzas/requests/${r.id}/decision`, { decision }, { params: { token: token() } });
      toast.success(decision === "approve" ? "Igazolás jóváhagyva, PDF elkészült" : "Kérelem elutasítva");
      load();
    } catch (err) { toast.error(getAuthErrorMessage(err)); }
    setBusy("");
  };
  return (
    <section className={card} data-testid="hianyzas-admin-pending">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-slate-900"><Clock size={20} className="text-yellow-600" /> Hiányzásigazolási kérelmek</h2>
      {!reqs ? <p className="py-6 text-center text-slate-600">Betöltés...</p> : reqs.length === 0 ? <p className="py-6 text-center text-sm text-slate-600" data-testid="hianyzas-pending-empty">Nincs függőben lévő hiányzásigazolási kérelem.</p> : (
        <ul className="divide-y divide-slate-100">{reqs.map((r) => {
          const paper = ["Igazolás", "Kikérő"].includes(r.type);
          const needsPdf = paper && !r.pdf_path;
          return (
          <RequestRow key={r.id} r={r}>
            {paper && (
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto" data-testid={`hianyzas-pdf-area-${r.id}`}>
                {r.pdf_path && <PdfButtons r={r} />}
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50">
                  <Upload size={14} /> {r.pdf_path ? "PDF cseréje" : "PDF feltöltése"}
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { upload(r, e.target.files?.[0]); e.target.value = ""; }} data-testid={`hianyzas-upload-${r.id}`} />
                </label>
                {needsPdf && <p className="w-full text-xs font-medium text-red-700" data-testid={`hianyzas-needs-pdf-${r.id}`}>Az elfogadáshoz töltsd fel az igazolás vagy kikérő PDF-jét.</p>}
              </div>
            )}
            <button type="button" disabled={busy === r.id || needsPdf} onClick={() => decide(r, "approve")} className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60" data-testid={`hianyzas-approve-${r.id}`}><Check size={16} /> Jóváhagyás</button>
            <button type="button" disabled={busy === r.id} onClick={() => decide(r, "reject")} className="inline-flex items-center gap-1 rounded-xl bg-red-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60" data-testid={`hianyzas-reject-${r.id}`}><X size={16} /> Elutasítás</button>
          </RequestRow>
          );
        })}</ul>
      )}
    </section>
  );
}

export default HianyzasTab;
